import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import type { ParsingError } from "../../types";
import type { Converter } from "../types";
import { ConverterErrorType } from "../types";
import {
  JSON_FIELDS,
  extractLines,
  extractLineValues,
  extractSensorValues,
  assignOptionalFields,
} from "../utils/extract";
import { isLeft } from "fp-ts/lib/Either";

const FIELD_NAME_MAPPING: Record<string, string> = {
  SOURCE: JSON_FIELDS.SOURCE_ID,
  DATEDEBUT: JSON_FIELDS.ACQUISITION_START_DATE,
  DATEFIN: JSON_FIELDS.ACQUISITION_END_DATE,
  CAMPAGNE: JSON_FIELDS.GROUP,
  HISTORIQUE_ESSAI: JSON_FIELDS.LABEL,
  TRANCHE: JSON_FIELDS.TRANCHE,
};

export const convertSourceSnapshotsContextToJSON: Converter = (input: string) => {
  const lines = extractLines<typeof FIELD_NAME_MAPPING>(input, FIELD_NAME_MAPPING);
  const [sourceId] = extractLineValues(lines, JSON_FIELDS.SOURCE_ID);
  const [tranche] = extractLineValues(lines, JSON_FIELDS.TRANCHE);
  const [acquisitionStartDate] = extractLineValues(lines, JSON_FIELDS.ACQUISITION_START_DATE);
  const [acquisitionEndDate] = extractLineValues(lines, JSON_FIELDS.ACQUISITION_END_DATE);
  const [label] = extractLineValues(lines, JSON_FIELDS.LABEL);
  const [group] = extractLineValues(lines, JSON_FIELDS.GROUP);
  const dateErrors: ParsingError<ConverterErrorType>[] = [];

  if (acquisitionStartDate === undefined || acquisitionStartDate?.length === 0) {
    dateErrors.push({
      kind: ConverterErrorType.MISSING_SNAPSHOT_DATE,
      explain: "snapshot date is missing",
      location: { file: "", line: 0 },
      data: {
        key: 0,
      },
    });
  }

  const { sensorValues, errors } = extractSensorValues(lines, 0, true, acquisitionStartDate);

  const snapshots =
    acquisitionStartDate && sensorValues.length > 0
      ? [
          {
            date: acquisitionStartDate,
            sensorValues,
            /**
             * Optional Fields
             * @link {back/src/api/snapshot/index.ts}
             */
            ...assignOptionalFields(acquisitionStartDate, acquisitionEndDate, label, group),
          },
        ]
      : [];

  const emptyResults =
    !sourceId && !snapshots.length
      ? [
          {
            kind: ConverterErrorType.EMPTY_DATA_EXTRACTION,
            explain: "no data extracted from file",
            location: { file: "", line: 0 },
            data: { value: { snapshots } },
          },
        ]
      : [];

  return {
    result: { sourceId: sourceId || tranche, snapshots },
    errors: [
      ...emptyResults,
      ...(sourceId && isLeft(short(io.string).decode(sourceId))
        ? [
            {
              kind: ConverterErrorType.INVALID_SOURCE_ID,
              explain: "invalid source id",
              location: { file: "", line: 0 },
              data: { value: sourceId },
            },
          ]
        : []),
      ...dateErrors,
      ...errors,
      ...(label && isLeft(short(io.string).decode(label))
        ? [
            {
              kind: ConverterErrorType.INVALID_LABEL_TOO_LONG,
              explain: "invalid label too long",
              location: { file: "", line: 0 },
              data: { value: label },
            },
          ]
        : []),
      ...(group && isLeft(short(io.string).decode(group))
        ? [
            {
              kind: ConverterErrorType.INVALID_GROUP_TOO_LONG,
              explain: "invalid group too long",
              location: { file: "", line: 0 },
              data: { value: group },
            },
          ]
        : []),
    ],
  };
};
